import React, { useState, useEffect } from 'react';
import { Event, getEvents } from '../api';
import { EventCard } from '../components/EventCard';
import { getTodayString, getTomorrowString, formatDate } from '../utils';

export function HomePage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(getTodayString());
  const [viewMode, setViewMode] = useState<'today' | 'tomorrow'>('today');

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await getEvents(currentDate);
      setEvents(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, viewMode]);

  function goToToday() {
    setCurrentDate(getTodayString());
    setViewMode('today');
  }

  function goToTomorrow() {
    setCurrentDate(getTomorrowString());
    setViewMode('tomorrow');
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-4xl font-bold text-gray-800 mb-2">
            {viewMode === 'today'
              ? "Today's Events"
              : "Tomorrow's Events"}
          </h2>
          <p className="text-xl text-gray-600">{formatDate(currentDate)}</p>
        </div>
        <div className="flex gap-4 flex-wrap">
          <button
            onClick={goToToday}
            className={`px-6 py-2 rounded font-semibold transition ${
              viewMode === 'today'
                ? 'bg-pink-500 text-white'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            }`}
          >
            Today
          </button>
          <button
            onClick={goToTomorrow}
            className={`px-6 py-2 rounded font-semibold transition ${
              viewMode === 'tomorrow'
                ? 'bg-pink-500 text-white'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            }`}
          >
            Tomorrow →
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Loading events...</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          <p>{error}</p>
        </div>
      )}

      {/* Events */}
      {!loading && events.length > 0 ? (
        <div className="space-y-4">
          {events.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      ) : (
        !loading && (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <span className="text-6xl mb-4">😴</span>
            <p className="text-gray-600 text-xl">No events scheduled for this day.</p>
            <p className="text-gray-500 mt-2">Check back soon or try another date!</p>
          </div>
        )
      )}
    </div>
  );
}
